import Close from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import LinearProgress from '@mui/material/LinearProgress';
import Typography from '@mui/material/Typography';
import { t } from 'i18next';
import { observer } from 'mobx-react-lite';
import { Link as RouterLink, useMatch } from 'react-router-dom';
import { useLocalStorage } from 'usehooks-ts';

import useBillingBannerState from '~components/BillingStatusBanner/useBillingBannerState';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { FeatureFlags } from '~constants/featureFlags';
import { AnalyticsController } from '~hooks/useAnalytics/AnalyticsController';
import { canManageCompanyOnboarding } from '~hooks/useCompanyOnboarding/companyOnboardingAccess';
import useCompanyOnboardingProgressQuery from '~queries/useCompanyOnboardingProgressQuery';
import {
  companyOnboardingChecklistPath,
  companyOnboardingStepPath,
} from '~router/routePaths';
import { isImpersonating } from '~services/sessionTokens';
import { useStores } from '~store';
import { useFeatureFlag } from '~utils/hooks/useFeatureFlag';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { hexToRgba } from '~utils/utilFunctions';

const POLL_INTERVAL_MS = 5 * 60_000;
const DISMISSAL_STORAGE_KEY_PREFIX = 'tread.companyOnboardingBanner.dismissed';

const CompanyOnboardingBanner = observer(() => {
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [isDismissed, setIsDismissed] = useLocalStorage(
    `${DISMISSAL_STORAGE_KEY_PREFIX}.${userStore.user.id}.${companyId}`,
    false,
  );
  const isFeatureFlagEnabled = useFeatureFlag({
    featureFlagKey: FeatureFlags.companyOnboarding,
  });
  const isOnOnboardingRoute = Boolean(useMatch(`${companyOnboardingChecklistPath}/*`));
  const isBillingBannerShowing = Boolean(useBillingBannerState());
  const canShowBanner =
    isFeatureFlagEnabled &&
    canManageCompanyOnboarding(userStore) &&
    !isImpersonating() &&
    !isDismissed &&
    !isBillingBannerShowing &&
    !isOnOnboardingRoute;
  const { completedCount, currentStepKey, hasLoaded, percentComplete, totalCount } =
    useCompanyOnboardingProgressQuery({
      companyId,
      isEnabled: canShowBanner,
      notifiesOnError: false,
      refetchIntervalMs: POLL_INTERVAL_MS,
    });
  const isVisible = canShowBanner && hasLoaded && completedCount < totalCount;

  if (!isVisible) {
    return null;
  }

  const trackContinueSetup = () =>
    AnalyticsController.track('company_onboarding_banner_clicked', {
      completed_step_count: completedCount,
      current_step_key: currentStepKey,
      total_step_count: totalCount,
    });

  const dismiss = () => {
    AnalyticsController.track('company_onboarding_banner_dismissed', {
      completed_step_count: completedCount,
      total_step_count: totalCount,
    });
    setIsDismissed(true);
  };

  return (
    <Box
      data-test-id="company-onboarding-banner"
      role="status"
      sx={(theme) => ({
        alignItems: 'center',
        bgcolor: hexToRgba(theme.brandV2.colors.treadBlue, 0.1),
        borderBottom: `1px solid ${hexToRgba(theme.brandV2.colors.treadBlue, 0.25)}`,
        display: 'flex',
        gap: 2,
        px: 5,
        py: 1.25,
      })}
    >
      <Box sx={{ flex: 1 }} />
      <Typography noWrap variant="body2" sx={{ color: 'text.primary', fontWeight: 700 }}>
        {t('company_onboarding.banner.title')}
      </Typography>
      <LinearProgress
        aria-label={t('company_onboarding.banner.title')}
        value={percentComplete}
        variant="determinate"
        sx={(theme) => ({
          bgcolor: theme.semantic.borderStrong,
          flexShrink: 0,
          width: 160,
          '& .MuiLinearProgress-bar': { bgcolor: theme.brandV2.colors.treadOrangeDark },
        })}
      />
      <Typography noWrap variant="body2" sx={{ color: 'text.secondary' }}>
        {t('company_onboarding.banner.progress', {
          completed: completedCount,
          percent: percentComplete,
          total: totalCount,
        })}
      </Typography>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          flex: 1,
          gap: 2,
          justifyContent: 'flex-end',
        }}
      >
        <SmallButton
          color="brandV2Black"
          component={RouterLink}
          onClick={trackContinueSetup}
          to={
            currentStepKey
              ? companyOnboardingStepPath(currentStepKey)
              : companyOnboardingChecklistPath
          }
          variant="contained"
          sx={(theme) => ({ color: theme.palette.common.white, flexShrink: 0 })}
        >
          {t('company_onboarding.banner.continue_setup')}
        </SmallButton>
        <IconButton
          aria-label={t('company_onboarding.banner.dismiss')}
          onClick={dismiss}
          size="small"
        >
          <Close sx={{ fontSize: 18 }} />
        </IconButton>
      </Box>
    </Box>
  );
});

export default CompanyOnboardingBanner;
